import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { rtdb } from "utils/firebase";
import { BASE_URL, environment } from 'utils/constant';
import { FileText, Image as ImageIcon, Download, Loader2, Archive } from 'lucide-react';
import { FaFilePdf } from "react-icons/fa";
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { getImageUrl } from "../../Project Dashboard/Snags/hooks/useMediaUrl";
import { fileTypeIcons } from 'utils/utilityFunctions';

const StorageBar = ({ stats, loading }) => {
    // Calculate percentages for the bar width
    // Avoid division by zero
    const total = stats.total || 1;
    const snagsPercent = (stats.snags / total) * 100;
    const financePercent = (stats.finance / total) * 100;
    const materialsPercent = (stats.materials / total) * 100;

    // Helper to format bytes
    const formatBytes = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <div className="w-full mb-8">
            <div className="flex justify-between items-end mb-2">
                <h2 className="text-xl font-bold font-outfit text-gray-800">Global Media View</h2>
            </div>
            {/* <div className="w-full h-4 bg-gray-100 rounded-full overflow-hidden flex border border-gray-200">
                <div
                    className="h-full bg-[#fde047] transition-all duration-1000 ease-out"
                    style={{ width: `${snagsPercent}%` }}
                ></div>
                <div
                    className="h-full bg-[#ec4899] transition-all duration-1000 ease-out"
                    style={{ width: `${financePercent}%` }}
                ></div>
                <div
                    className="h-full bg-[#5b21b6] transition-all duration-1000 ease-out"
                    style={{ width: `${materialsPercent}%` }}
                ></div>
                <div className="h-full bg-transparent flex-grow"></div>
            </div> */}
            <div className="flex justify-between mt-2 text-sm text-gray-600 font-medium">
                <div className="flex gap-4">
                    <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-[#fde047]"></span>
                        Snags ({formatBytes(stats.snags)})
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-[#ec4899]"></span>
                        Finance ({formatBytes(stats.finance)})
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-[#5b21b6]"></span>
                        Procurement ({formatBytes(stats.materials)})
                    </span>
                </div>
                <div>
                    {loading ? (
                        <span className="flex items-center gap-2 text-gray-400">
                            <Loader2 className="w-3 h-3 animate-spin" /> Calculating storage...
                        </span>
                    ) : (
                        <span>Total Used: {formatBytes(stats.total)}</span>
                    )}
                </div>
            </div>
        </div>
    );
};

const MediaItem = ({ item }) => {
    const [downloading, setDownloading] = useState(false);
    const fileName = item?.url ? item?.url?.split("?")[0].split("/").pop() : "Document";
    const extension = fileName.split(".").pop().toLowerCase();
    const handleDownload = async (e) => {
        e.stopPropagation();
        setDownloading(true);
        try {
            const response = await axios.get(item.url, {
                responseType: 'blob'
            });
            saveAs(response.data, item.name);
        } catch (error) {
            console.error("Error downloading file:", error);
            // Fallback: open in new tab if blob download fails
            window.open(item.url, '_blank');
        } finally {
            setDownloading(false);
        }
    };

    const getIcon = (url) => {
        // if (url?.toLowerCase().includes('.pdf')) {
        //     return <FaFilePdf className="w-6 h-6 text-red-500" />;
        // }
        return fileTypeIcons({
            fileType: extension,
            className: "w-5 h-5 text-purple-600 flex-shrink-0",
        })
    };


    const getBadgeColor = (type) => {
        switch (type) {
            case 'Finance': return 'bg-pink-100 text-pink-700 border-pink-200';
            case 'Materials': return 'bg-purple-100 text-purple-700 border-purple-200';
            case 'Snags': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
            default: return 'bg-gray-100 text-gray-700 border-gray-200';
        }
    };

    return (
        <div className="group bg-white border border-gray-200 rounded-lg px-3 py-2 flex items-center justify-between hover:shadow-md transition-all duration-200 mb-3">
            <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                    {getIcon(item.url)}
                </div>
                <div className="flex flex-col min-w-0">
                    <span className="font-semibold text-sm text-gray-800 truncate pr-4">{item.name}</span>
                </div>
            </div>

            <div className="flex items-center justify-end gap-4 flex-shrink-0">
                {/* Source Column: Fixed width wrapper to maintain alignment even if source is missing */}
                <div className="w-24 hidden sm:flex justify-center">
                    {item.source ? (
                        <div className="px-3 py-1 rounded-full border text-xs font-semibold w-full text-center bg-gray-100 text-gray-700 border-gray-200 truncate">
                            {item.source}
                        </div>
                    ) : (
                        <div className="w-full"></div> // Spacer to keep alignment
                    )}
                </div>

                {/* Type Column: Fixed width */}
                <div className="w-24 flex justify-center">
                    <div className={`px-3 py-1 rounded-full border text-xs font-semibold w-full text-center ${getBadgeColor(item.type)}`}>
                        {item.type}
                    </div>
                </div>

                {/* Uploaded By Column: Fixed width */}
                <div className="hidden sm:flex flex-col items-end text-xs text-gray-500 w-[140px]">
                    <span className="font-medium text-gray-700 truncate w-full text-right" title={`Uploaded by : ${item.uploaded_by}`}>
                        Uploaded by : {item.uploaded_by}
                    </span>
                    <span className="truncate w-full text-right">{item.date}</span>
                </div>

                {/* Download Action */}
                <button
                    onClick={handleDownload}
                    disabled={downloading}
                    className="p-2 hover:bg-gray-100 rounded-full text-gray-500 hover:text-gray-900 transition-colors disabled:opacity-50 flex-shrink-0"
                    title="Download"
                >
                    {downloading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
                </button>
            </div>
        </div>
    );
};

export default function GlobalMedia() {
    const [activeTab, setActiveTab] = useState('All');
    const [mediaItems, setMediaItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [calculatingStorage, setCalculatingStorage] = useState(false);
    const [downloadingAll, setDownloadingAll] = useState(false);
    const [storageStats, setStorageStats] = useState({
        finance: 0,
        materials: 0,
        snags: 0,
        total: 0
    });

    const tabs = ['All', 'Snags', 'Finance', 'Materials'];

    const getOrgId = () => {
        try {
            const org = JSON.parse(localStorage.getItem('organization'));
            return org?.id;
        } catch (e) {
            console.error("Error parsing organization from local storage", e);
            return null;
        }
    };

    const fetchSnagsMedia = async (orgId) => {
        try {
            const allSnagMedia = [];
            const processedMediaIds = new Set(); // To avoid duplicates if any

            // Helper to process messages snapshot
            const processMessages = async (snapshot, sourceName) => {
                if (!snapshot.exists()) return;

                const messages = [];
                snapshot.forEach(child => {
                    messages.push(child.val());
                });

                const mediaPromises = messages
                    .filter(msg => msg.has_media && msg.media && Array.isArray(msg.media))
                    .flatMap(msg => {
                        // msg.media is an array of objects: { mediaId, type, url (maybe), ... }
                        return (msg.media || []).map(async (mediaItem) => {
                            if (!mediaItem.mediaId || processedMediaIds.has(mediaItem.mediaId) || mediaItem.type === "audio") return null;

                            processedMediaIds.add(mediaItem.mediaId);

                            // Fetch actual URL using the hook/utility
                            const snagObject = await getImageUrl(mediaItem.mediaId);
                            let url = "";
                            if (snagObject?.type === "image" || snagObject?.type === "video") {
                                url = snagObject?.image;
                            }
                            else {
                                url = snagObject?.file;
                            }

                            if (!url) return null;
                            const fileName = url ? url?.split("?")[0].split("/").pop() : "Document";
                            const extension = fileName.split(".").pop().toLowerCase();
                            return {
                                id: mediaItem.mediaId,
                                url: url,
                                name: fileName,
                                type: 'Snags',
                                source: sourceName,
                                uploaded_by: msg.senderName || 'Unknown',
                                date: new Date(msg.createdAt).toLocaleDateString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true })
                            };
                        });
                    });

                const mediaItems = await Promise.all(mediaPromises);
                allSnagMedia.push(...mediaItems.filter(item => item !== null));
            };

            // 1. Fetch Global Snags
            const globalSnagsRef = rtdb.ref(`global_snags-${environment}/${orgId}`);
            const globalSnapshot = await globalSnagsRef.once('value');

            if (globalSnapshot.exists()) {
                const processGlobalPromises = [];
                globalSnapshot.forEach(child => {
                    const key = child.key;
                    // 'general_updates' or specific snag ID
                    const messagesRef = rtdb.ref(`global_snags-${environment}/${orgId}/${key}/messages`);
                    processGlobalPromises.push(messagesRef.once('value').then(snap => processMessages(snap, "Global")));
                });
                await Promise.all(processGlobalPromises);
            }

            // 2. Fetch Sites to get Project Snags
            const token = localStorage.getItem('access');
            const sitesRes = await axios.get(`${BASE_URL}/sites/organizations/${orgId}/sites/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const sites = sitesRes.data || [];
            // Assuming sitesRes.data.data is the array of sites. Adjust if structure matches API better.
            // double check sites structure
            // The user said: /sites/organizations/${organizationId}/sites/

            // 3. Fetch Project Snags for each site
            const sitePromises = sites.map(async (site) => {
                const siteId = site.id;
                const siteSnagsRef = rtdb.ref(`site_snags-${environment}/${siteId}`);
                const siteSnapshot = await siteSnagsRef.once('value');

                if (siteSnapshot.exists()) {
                    const processSitePromises = [];
                    siteSnapshot.forEach(child => {
                        const key = child.key; // snag ID or 'general_updates'
                        const messagesRef = rtdb.ref(`site_snags-${environment}/${siteId}/${key}/messages`);
                        processSitePromises.push(messagesRef.once('value').then(snap => processMessages(snap, site.site_name)));
                    });
                    await Promise.all(processSitePromises);
                }
            });

            await Promise.all(sitePromises);

            return allSnagMedia;

        } catch (error) {
            console.error("Error fetching snag media:", error);
            return [];
        }
    };

    const fetchMedia = async () => {
        const orgId = getOrgId();
        const token = localStorage.getItem('access');

        if (!orgId || !token) {
            console.error("Missing credentials");
            setLoading(false);
            return;
        }

        try {
            const [financeRes, materialRes, snagsData] = await Promise.all([
                axios.get(`${BASE_URL}/finance/receipt-images/org/${orgId}/`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }).catch(err => ({ data: [] })), // Handle individual failure gracefully
                axios.get(`${BASE_URL}/inventory/receipt-images/${orgId}/`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }).catch(err => ({ data: [] })),
                fetchSnagsMedia(orgId)
            ]);

            const financeData = (Array.isArray(financeRes.data) ? financeRes.data : [])
                .map(url => processUrl(url, 'Finance'));

            const materialData = (Array.isArray(materialRes.data) ? materialRes.data : [])
                .map(url => processUrl(url, 'Materials'));

            const allItems = [...financeData, ...materialData, ...snagsData];
            setMediaItems(allItems);

            // Trigger storage calculation after setting items
            calculateStorage(allItems);

        } catch (error) {
            console.error("Error fetching media:", error);
        } finally {
            setLoading(false);
        }
    };

    const calculateStorage = async (items) => {
        setCalculatingStorage(true);
        let financeSize = 0;
        let materialsSize = 0;
        let snagsSize = 0;

        // Create an array of promises to fetch sizes in parallel
        // Note: For very large lists, we might want to batch this to avoid browser limits
        const sizePromises = items.map(async (item) => {
            if (!item.url || item.url === '#') return 0; // Skip mock data or invalid URLs

            try {
                const response = await axios.head(item.url);
                const size = parseInt(response.headers['content-length'], 10) || 0;

                if (item.type === 'Finance') financeSize += size;
                else if (item.type === 'Materials') materialsSize += size;
                else if (item.type === 'Snags') snagsSize += size;

                return size;
            } catch (error) {
                // If HEAD fails (e.g. CORS), we just ignore the size for now
                console.warn(`Failed to fetch size for ${item.name}`, error);
                return 0;
            }
        });

        await Promise.allSettled(sizePromises);

        setStorageStats({
            finance: financeSize,
            materials: materialsSize,
            snags: snagsSize, // Mock data will be 0 unless we hardcode it
            total: financeSize + materialsSize + snagsSize
        });
        setCalculatingStorage(false);
    };

    const processUrl = (url, type) => {
        // Extract filename from URL (decodeURI to handle %20 etc)
        let name = "Unknown File";
        try {
            const urlObj = new URL(url);
            const pathParts = urlObj.pathname.split('/');
            name = decodeURIComponent(pathParts[pathParts.length - 1]);
        } catch (e) {
            name = url.split('/').pop().split('?')[0];
        }

        return {
            id: url, // using URL as ID for now
            url: url,
            name: name,
            type: type,
            uploaded_by: 'User', // Mocked as requested ('uploaded_by' fields missing)
            date: new Date().toLocaleDateString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true }) // Mocked date or parse from SAS token if valid
        };
    };

    const handleDownloadAll = async () => {
        // Filter items based on current view
        const itemsToDownload = filteredItems;
        if (itemsToDownload.length === 0) return;

        setDownloadingAll(true);
        const zip = new JSZip();

        // Batch requests to prevent overwhelming the browser/server
        const batchSize = 5;
        for (let i = 0; i < itemsToDownload.length; i += batchSize) {
            const batch = itemsToDownload.slice(i, i + batchSize);
            await Promise.all(batch.map(async (item) => {
                if (!item.url || item.url === '#') return;
                try {
                    const response = await axios.get(item.url, { responseType: 'blob' });
                    // Ensure unique filenames in zip
                    let fileName = item.name;
                    if (zip.file(fileName)) {
                        const parts = fileName.split('.');
                        const ext = parts.pop();
                        const base = parts.join('.');
                        fileName = `${base}_${Date.now()}.${ext}`;
                    }
                    zip.file(fileName, response.data);
                } catch (error) {
                    console.error(`Failed to download ${item.name} for zip`, error);
                }
            }));
        }

        try {
            const content = await zip.generateAsync({ type: "blob" });
            const zipName = activeTab === 'All' ? 'global_media_all.zip' : `global_media_${activeTab.toLowerCase()}.zip`;
            saveAs(content, zipName);
        } catch (error) {
            console.error("Error generating zip:", error);
            alert("Failed to generate zip file.");
        } finally {
            setDownloadingAll(false);
        }
    };

    useEffect(() => {
        fetchMedia();
    }, []);

    const filteredItems = activeTab === 'All'
        ? mediaItems
        : mediaItems.filter(item => item.type === activeTab);

    return (
        <div className="p-4 w-[85%] mx-auto">
            <StorageBar stats={storageStats} loading={calculatingStorage} />

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <div className="flex gap-4 overflow-x-auto pb-2 w-full sm:w-auto">
                    {tabs.map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-6 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap border ${activeTab === tab
                                ? 'bg-gray-900 text-white border-gray-900 shadow-md'
                                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                <button
                    onClick={handleDownloadAll}
                    disabled={downloadingAll || filteredItems.length === 0}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium whitespace-nowrap"
                >
                    {downloadingAll ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Preparing Zip...
                        </>
                    ) : (
                        <>
                            <Archive className="w-4 h-4" />
                            Download All ({filteredItems.length})
                        </>
                    )}
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center items-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
            ) : (
                <div className="space-y-3  h-[63vh] overflow-y-scroll">
                    {filteredItems.length > 0 ? (
                        filteredItems.map((item, index) => (
                            <MediaItem key={index} item={item} />
                        ))
                    ) : (
                        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                            No media files found in this category.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
